import { useState } from 'react'
import {
  TrendingUp,
  AlertTriangle,
  Loader2,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  CheckCircle,
  XCircle,
  Coins,
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { StatsCard } from '@/components/dashboard/StatsCard'
import { DashboardSectionHeader } from '@/components/dashboard/DashboardSectionHeader'
import { cn } from '@/lib/utils'
import { truncateAddress } from '@/lib/utils'
import { usePositions, type PositionData } from '@/hooks/usePositions'
import { SEEDBOT_BASE_URL } from '@/hooks/useSeedbotHealth'
import { useQueryClient } from '@tanstack/react-query'

function getPlatformBadge(platform: string) {
  switch (platform) {
    case 'dextoro':
      return { label: 'dextoro', className: 'bg-green-500/10 text-green-500' }
    case 'pumpfun':
      return { label: 'pump.fun', className: 'bg-orange-500/10 text-orange-400' }
    default:
      return { label: 'Unknown', className: 'bg-zinc-500/10 text-zinc-400' }
  }
}

function getSolscanUrl(mint: string): string {
  return `https://solscan.io/token/${mint}`
}

export function PositionsPage() {
  const { data, isLoading, error } = usePositions()
  const queryClient = useQueryClient()
  const positions = data?.positions ?? []

  const [expandedMint, setExpandedMint] = useState<string | null>(null)
  const [sellingKey, setSellingKey] = useState<string | null>(null)
  const [sellResult, setSellResult] = useState<{
    success: boolean
    message: string
    txSignature?: string
  } | null>(null)

  const dextoroCount = positions.filter((p) => p.platform === 'dextoro').length
  const pumpfunCount = positions.filter((p) => p.platform === 'pumpfun').length

  async function handleSell(mint: string, walletAddress: string, platform: string) {
    const confirmMsg = `Sell all tokens of ${mint.slice(0, 8)}... from wallet ${walletAddress.slice(0, 8)}...?`
    if (!window.confirm(confirmMsg)) return

    const key = `${mint}-${walletAddress}`
    setSellingKey(key)
    setSellResult(null)

    try {
      const res = await fetch(`${SEEDBOT_BASE_URL}/sell-position`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mint, walletAddress, platform }),
      })
      const resData = await res.json()

      if (!res.ok) {
        const errorMsg = res.status === 409
          ? 'Sell already in progress'
          : res.status === 503
            ? 'Seedbot still initializing'
            : resData.error || 'Sell failed'
        setSellResult({ success: false, message: errorMsg })
      } else {
        setSellResult({
          success: true,
          message: 'Sold successfully',
          txSignature: resData.txSignature,
        })
        queryClient.invalidateQueries({ queryKey: ['positions'] })
      }
    } catch (err) {
      setSellResult({
        success: false,
        message: err instanceof Error ? err.message : 'Network error',
      })
    } finally {
      setSellingKey(null)
    }
  }

  async function handleSellAll(position: PositionData) {
    const confirmMsg = `Sell ALL ${position.ticker || truncateAddress(position.mint, 4)} from ${position.wallets.length} wallet(s)?`
    if (!window.confirm(confirmMsg)) return

    setSellResult(null)
    for (const w of position.wallets) {
      const key = `${position.mint}-${w.walletAddress}`
      setSellingKey(key)
      try {
        const res = await fetch(`${SEEDBOT_BASE_URL}/sell-position`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            mint: position.mint,
            walletAddress: w.walletAddress,
            platform: position.platform,
          }),
        })
        const resData = await res.json()
        if (!res.ok) {
          setSellResult({ success: false, message: resData.error || `Failed for wallet ${truncateAddress(w.walletAddress, 4)}` })
          break
        }
      } catch (err) {
        setSellResult({ success: false, message: err instanceof Error ? err.message : 'Network error' })
        break
      }
    }
    setSellingKey(null)
    setSellResult((prev) => prev ?? { success: true, message: `Sold from ${position.wallets.length} wallet(s)` })
    queryClient.invalidateQueries({ queryKey: ['positions'] })
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Page Header */}
      <div>
        <h2 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
          <TrendingUp className="h-6 w-6 text-brand-fg" />
          Positions
        </h2>
        <p className="text-sm sm:text-base text-muted-foreground">
          Token holdings across all launcher and bundle wallets
        </p>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3">
          <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />
          <span className="text-sm text-red-500">
            Unable to fetch positions. Seedbot may be down.
          </span>
        </div>
      )}

      {/* Sell result banner */}
      {sellResult && (
        <div className={cn(
          'flex items-center gap-2 rounded-lg border p-3 text-sm',
          sellResult.success
            ? 'border-green-500/30 bg-green-500/10 text-green-500'
            : 'border-red-500/30 bg-red-500/10 text-red-500'
        )}>
          {sellResult.success ? (
            <CheckCircle className="h-4 w-4 shrink-0" />
          ) : (
            <XCircle className="h-4 w-4 shrink-0" />
          )}
          <span>{sellResult.message}</span>
          {sellResult.txSignature && (
            <a
              href={`https://solscan.io/tx/${sellResult.txSignature}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-brand-fg hover:underline ml-1"
            >
              View tx <ExternalLink className="h-3 w-3" />
            </a>
          )}
          <button
            onClick={() => setSellResult(null)}
            className="ml-auto text-xs opacity-60 hover:opacity-100"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Stats */}
      <div>
        <DashboardSectionHeader icon={Coins} title="Summary" />
        <div className="grid gap-3 sm:gap-4 grid-cols-2 sm:grid-cols-3">
          <StatsCard title="Unique Tokens" value={positions.length} icon={Coins} loading={isLoading} />
          <StatsCard title="dextoro Tokens" value={dextoroCount} icon={TrendingUp} loading={isLoading} />
          <StatsCard title="pump.fun Tokens" value={pumpfunCount} icon={TrendingUp} loading={isLoading} />
        </div>
      </div>

      {/* Positions Table */}
      <div>
        <DashboardSectionHeader icon={TrendingUp} title="Token Holdings" />
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            {isLoading ? (
              <div className="p-4 space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-8 bg-muted rounded animate-pulse" />
                ))}
              </div>
            ) : positions.length > 0 ? (
              <table className="w-full text-sm min-w-[780px]">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-3 sm:p-4 text-xs font-medium text-muted-foreground w-8" />
                    <th className="text-left p-3 sm:p-4 text-xs font-medium text-muted-foreground">Platform</th>
                    <th className="text-left p-3 sm:p-4 text-xs font-medium text-muted-foreground">Token</th>
                    <th className="text-left p-3 sm:p-4 text-xs font-medium text-muted-foreground">Ticker</th>
                    <th className="text-right p-3 sm:p-4 text-xs font-medium text-muted-foreground">Total Amount</th>
                    <th className="text-right p-3 sm:p-4 text-xs font-medium text-muted-foreground">Wallets</th>
                    <th className="text-right p-3 sm:p-4 text-xs font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {positions.map((pos) => {
                    const badge = getPlatformBadge(pos.platform)
                    const isExpanded = expandedMint === pos.mint
                    const isSelling = !!sellingKey?.startsWith(pos.mint)

                    return (
                      <>
                        <tr
                          key={pos.mint}
                          className="border-b border-border hover:bg-muted/30 transition-colors cursor-pointer"
                          onClick={() => setExpandedMint(isExpanded ? null : pos.mint)}
                        >
                          <td className="p-3 sm:p-4">
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            )}
                          </td>
                          <td className="p-3 sm:p-4">
                            <span className={cn('inline-flex px-2 py-0.5 rounded-full text-xs font-medium', badge.className)}>
                              {badge.label}
                            </span>
                          </td>
                          <td className="p-3 sm:p-4">
                            <div className="flex items-center gap-1">
                              <span className="font-medium">{pos.name || truncateAddress(pos.mint, 4)}</span>
                              <a
                                href={getSolscanUrl(pos.mint)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-brand-fg hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            </div>
                          </td>
                          <td className="p-3 sm:p-4 font-mono text-xs">{pos.ticker || '-'}</td>
                          <td className="p-3 sm:p-4 text-right font-medium">{pos.totalAmountFormatted}</td>
                          <td className="p-3 sm:p-4 text-right text-muted-foreground">{pos.wallets.length}</td>
                          <td className="p-3 sm:p-4 text-right">
                            {pos.platform !== 'unknown' && (
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={isSelling}
                                onClick={(e) => { e.stopPropagation(); handleSellAll(pos) }}
                              >
                                {isSelling ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  'Sell All'
                                )}
                              </Button>
                            )}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr key={`${pos.mint}-expanded`} className="border-b border-border bg-muted/20">
                            <td colSpan={7} className="p-0">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b border-border/50">
                                    <th className="text-left p-2 pl-12 text-xs font-medium text-muted-foreground">Wallet</th>
                                    <th className="text-left p-2 text-xs font-medium text-muted-foreground">Type</th>
                                    <th className="text-right p-2 text-xs font-medium text-muted-foreground">Balance</th>
                                    <th className="text-right p-2 pr-4 text-xs font-medium text-muted-foreground">Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {pos.wallets.map((w) => {
                                    const walletKey = `${pos.mint}-${w.walletAddress}`
                                    const isWalletSelling = sellingKey === walletKey
                                    return (
                                      <tr key={walletKey} className="border-b border-border/30 last:border-b-0">
                                        <td className="p-2 pl-12 font-mono text-xs">{truncateAddress(w.walletAddress, 4)}</td>
                                        <td className="p-2 text-xs text-muted-foreground capitalize">
                                          {w.walletType} #{w.walletIndex + 1}
                                        </td>
                                        <td className="p-2 text-right font-medium">{w.amountFormatted}</td>
                                        <td className="p-2 pr-4 text-right">
                                          {pos.platform !== 'unknown' && (
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              disabled={!!sellingKey}
                                              onClick={() => handleSell(pos.mint, w.walletAddress, pos.platform)}
                                            >
                                              {isWalletSelling ? (
                                                <Loader2 className="h-3 w-3 animate-spin" />
                                              ) : (
                                                'Sell'
                                              )}
                                            </Button>
                                          )}
                                        </td>
                                      </tr>
                                    )
                                  })}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </>
                    )
                  })}
                </tbody>
              </table>
            ) : (
              <div className="p-6 text-center text-sm text-muted-foreground">
                No token positions found across any wallets
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
